import {
  StatusTag,
  StatusTagType,
} from "@certn/cascadia-react/components/tags/StatusTag";
import { TagSize } from "@certn/cascadia-react/components/tags/_Tag";

export function renderLegacyStatusTag({
  status,
  score,
  hasPopover = false,
  size,
}: {
  status?: string | null;
  score?: string | null;
  hasPopover?: boolean;
  size?: TagSize;
}): React.ReactElement | null {
  if (!status) return null;

  const statusTagProps = getLegacyStatusTagPropsForStatus(status);
  const statusLabel = getLegacyStatusLabel(status);

  const scoreTagProps = score ? getLegacyScoreTagProps(score) : null;
  const scoreLabel = score ? getLegacyScoreLabel(score) : null;

  return (
    <>
      <StatusTag
        isButton={hasPopover}
        statusInfoIcon={hasPopover}
        size={size}
        {...(statusTagProps as StatusTagType)}
      >
        {statusLabel}
      </StatusTag>
      {score && scoreTagProps && (
        <StatusTag
          isButton={hasPopover}
          statusInfoIcon={hasPopover}
          size={size}
          {...(scoreTagProps as StatusTagType)}
        >
          {scoreLabel}
        </StatusTag>
      )}
    </>
  );
}

export function getLegacyStatusLabel(status: string): string {
  switch (status) {
    case "ACTION_REQUIRED":
      return "Action required";
    case "CANCELLED":
      return "Cancelled";
    case "COMPLETE":
      return "Complete";
    case "IN_DISPUTE":
      return "In dispute";
    case "IN_PROGRESS":
      return "In progress";
    case "WAITING_ON_CANDIDATE":
      return "Waiting on candidate";
    case "APPLICATION_EXPIRED":
      return "Application expired";
    default:
      return status;
  }
}

export function getLegacyScoreLabel(score: string): string {
  switch (score) {
    case "PASS":
      return "Pass";
    case "WARN":
      return "Warn";
    case "FAIL":
      return "Fail";
    case "CLEARED":
      return "Clear";
    case "REVIEW":
      return "Review";
    case "NA":
      return "N/A";
    case "DIDNOTPASS":
      return "Did Not Pass";
    case "NONE":
      return "None";
    default:
      return score;
  }
}

function getLegacyStatusTagPropsForStatus(status: string) {
  switch (status) {
    case "IN_PROGRESS":
      return {
        leftIcon: "document_scanner",
        fill: "background-accent-tofino-weak",
        color: "text-accent-tofino-strong",
      };
    case "ACTION_REQUIRED":
      return {
        leftIcon: "emoji_flags",
        fill: "background-accent-red-weak",
        color: "text-accent-red-strong",
      };
    case "WAITING_ON_CANDIDATE":
      return {
        leftIcon: "pending",
        fill: "background-accent-neutral-medium",
        color: "text-base",
      };
    case "IN_DISPUTE":
      return {
        leftIcon: "hourglass_top",
        fill: "background-accent-purple-weak",
        color: "text-accent-purple-strong",
      };
    case "CANCELLED":
      return {
        leftIcon: "block",
        fill: "background-accent-red-weak",
        color: "text-accent-red-strong",
      };
    case "COMPLETE":
      return {
        leftIcon: "inventory",
        fill: "background-accent-green-weak",
        color: "text-accent-green-strong",
      };
    case "APPLICATION_EXPIRED":
      return {
        leftIcon: "schedule",
        fill: "background-accent-orange-weak",
        color: "text-accent-orange-strong",
      };
    default:
      return {
        leftIcon: "pending",
        fill: "background-accent-neutral-medium",
        color: "text-base",
      };
  }
}

function getLegacyScoreTagProps(score?: string | null) {
  if (!score) return;

  switch (score) {
    case "PASS":
    case "CLEARED":
      return {
        leftIcon: "inventory",
        fill: "background-accent-green-weak",
        color: "text-accent-green-strong",
      };
    case "WARN":
    case "REVIEW":
      return {
        leftIcon: "quick_reference_all",
        fill: "background-accent-orange-weak",
        color: "text-accent-orange-strong",
      };
    case "FAIL":
    case "DIDNOTPASS":
      return {
        leftIcon: "quick_reference_all",
        fill: "background-accent-red-weak",
        color: "text-accent-red-strong",
      };
    case "NA":
    case "NONE":
      return {
        leftIcon: "pending",
        fill: "background-accent-neutral-medium",
        color: "text-base",
      };
    default:
      return {
        leftIcon: "quick_reference_all",
        fill: "background-accent-neutral-medium",
        color: "text-base",
      };
  }
}
